// src/pages/Userspage/UserDetails.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { usersService, jobsService } from "../../services";
import "./UserDetails.css";

// Role badge helper
const getRoleClass = (role) => {
  const roleMap = {
    'Admin': 'users-role-admin',
    'Super Admin': 'users-role-admin',
    'Moderator': 'users-role-moderator',
    'Customer': 'users-role-customer',
    'Driver': 'users-role-driver',
    'Guest': 'users-role-guest',
  };
  const roleName = typeof role === 'object' ? role?.name : role;
  return roleMap[roleName] || 'users-role-guest';
};

export default function UserDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [roles, setRoles] = useState([]);

  // Edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: '',
  });
  const [saving, setSaving] = useState(false);

  // Suspend modal
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');

  // Fetch user details
  const fetchUser = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const userData = await usersService.getUserById(id);
      setUser(userData);

      // Pre-fill edit form
      setEditForm({
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        email: userData.email || '',
        phone: userData.phone || '',
        role: userData.role?._id || '',
      });
    } catch (err) {
      console.error('Fetch user error:', err);
      setError('Failed to load user details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Fetch user's jobs (for customers)
  const fetchUserJobs = useCallback(async () => {
    if (!user || getRoleName(user.role) !== 'Customer') return;

    try {
      setJobsLoading(true);
      const response = await jobsService.getJobs({ client: id, limit: 10 });
      setJobs(response.jobs || []);
    } catch (err) {
      console.error('Fetch user jobs error:', err);
    } finally {
      setJobsLoading(false);
    }
  }, [id, user]);

  // Fetch roles
  const fetchRoles = async () => {
    try {
      const rolesData = await usersService.getRoles();
      setRoles(rolesData || []);
    } catch (err) {
      console.error('Fetch roles error:', err);
    }
  };

  useEffect(() => {
    fetchUser();
    fetchRoles();
  }, [fetchUser]);

  useEffect(() => {
    if (user) {
      fetchUserJobs();
    }
  }, [user, fetchUserJobs]);

  // Handle toggle status
  const handleToggleStatus = async () => {
    if (!user) return;

    try {
      await usersService.toggleStatus(user._id, !user.isActive);
      fetchUser();
    } catch (err) {
      console.error('Toggle status error:', err);
      alert('Failed to update user status');
    }
  };

  // Handle suspend
  const handleSuspend = async () => {
    if (!user) return;

    try {
      setSaving(true);
      await usersService.suspendUser(user._id, suspendReason);
      setShowSuspendModal(false);
      setSuspendReason('');
      fetchUser();
    } catch (err) {
      console.error('Suspend user error:', err);
      alert('Failed to suspend user');
    } finally {
      setSaving(false);
    }
  };

  // Handle unsuspend
  const handleUnsuspend = async () => {
    if (!user || !window.confirm('Are you sure you want to unsuspend this user?')) return;

    try {
      await usersService.unsuspendUser(user._id);
      fetchUser();
    } catch (err) {
      console.error('Unsuspend user error:', err);
      alert('Failed to unsuspend user');
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!user || !window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;

    try {
      await usersService.deleteUser(user._id);
      navigate('/users');
    } catch (err) {
      console.error('Delete user error:', err);
      alert('Failed to delete user');
    }
  };

  // Handle reset password
  const handleResetPassword = async () => {
    if (!user || !window.confirm('Are you sure you want to reset this user\'s password?')) return;

    try {
      const result = await usersService.resetPassword(user._id);
      alert(`Password reset successfully. New password: ${result.temporaryPassword || 'Sent via email'}`);
    } catch (err) {
      console.error('Reset password error:', err);
      alert('Failed to reset password');
    }
  };

  // Handle update user
  const handleUpdateUser = async () => {
    if (!user || !editForm.firstName || !editForm.email) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);

      const updates = {
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        email: editForm.email,
        phone: editForm.phone,
      };

      // Include role if changed
      if (editForm.role && editForm.role !== user.role?._id) {
        updates.role = editForm.role;
      }

      await usersService.updateUser(user._id, updates);

      setShowEditModal(false);
      fetchUser();
    } catch (err) {
      console.error('Update user error:', err);
      alert(err.message || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  // Get role name
  const getRoleName = (role) => {
    if (typeof role === 'object') return role?.name || 'N/A';
    return role || 'N/A';
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Format datetime
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Get status class
  const getStatusClass = (status) => {
    const statusMap = {
      'Pending': 'status-pending',
      'Assigned': 'status-assigned',
      'In Transit': 'status-transit',
      'Delivered': 'status-delivered',
      'Exception': 'status-exception',
      'Cancelled': 'status-cancelled',
    };
    return statusMap[status] || 'status-pending';
  };

  if (loading) {
    return (
      <div className="user-details-page">
        <div className="user-details-loading">
          <div className="user-details-spinner"></div>
          <p>Loading user details...</p>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="user-details-page">
        <div className="user-details-error">
          <p>{error || 'User not found'}</p>
          <button onClick={() => navigate('/users')}>Back to Users</button>
        </div>
      </div>
    );
  }

  const isCustomer = getRoleName(user.role) === 'Customer';
  const isDriver = getRoleName(user.role) === 'Driver';

  return (
    <div className="user-details-page">
      {/* Header */}
      <div className="mq-welcome-block">
        <button className="mq-back-btn" onClick={() => navigate('/users')}>
          <span className="mq-back-btn-icon">
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
              <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
          Back to Users
        </button>
        <h1 className="mq-welcome-name">User Details</h1>
      </div>

      {/* Main Content */}
      <div className="user-details-content">
        {/* Left Column - Profile */}
        <div className="user-details-left">
          {/* Profile Card */}
          <div className="user-profile-card">
            <div className="user-profile-header">
              <div className="user-profile-avatar">
                {user.avatar ? (
                  <img src={user.avatar} alt={user.firstName} />
                ) : (
                  user.firstName?.[0] || 'U'
                )}
              </div>
              <div className="user-profile-info">
                <h2>{user.firstName} {user.lastName}</h2>
                <span className={`users-role-badge ${getRoleClass(user.role)}`}>
                  {getRoleName(user.role)}
                </span>
              </div>
              <div className="user-profile-status">
                {user.isSuspended ? (
                  <span className="user-status-badge suspended">Suspended</span>
                ) : user.isActive ? (
                  <span className="user-status-badge active">Active</span>
                ) : (
                  <span className="user-status-badge inactive">Inactive</span>
                )}
              </div>
            </div>

            <div className="user-profile-details">
              <div className="user-detail-row">
                <span className="user-detail-label">Email</span>
                <span className="user-detail-value">
                  {user.email}
                  {user.isEmailVerified ? (
                    <span className="user-verified" title="Verified">✓</span>
                  ) : (
                    <span className="user-unverified" title="Not Verified">!</span>
                  )}
                </span>
              </div>
              <div className="user-detail-row">
                <span className="user-detail-label">Phone</span>
                <span className="user-detail-value">{user.phone || 'Not provided'}</span>
              </div>
              <div className="user-detail-row">
                <span className="user-detail-label">Joined</span>
                <span className="user-detail-value">{formatDate(user.createdAt)}</span>
              </div>
              <div className="user-detail-row">
                <span className="user-detail-label">Last Login</span>
                <span className="user-detail-value">{formatDateTime(user.lastLogin)}</span>
              </div>
              {user.loginCount > 0 && (
                <div className="user-detail-row">
                  <span className="user-detail-label">Login Count</span>
                  <span className="user-detail-value">{user.loginCount}</span>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="user-profile-actions">
              <button className="user-action-btn edit" onClick={() => setShowEditModal(true)}>
                Edit Profile
              </button>
              <button className="user-action-btn" onClick={handleToggleStatus}>
                {user.isActive ? 'Deactivate' : 'Activate'}
              </button>
              {user.isSuspended ? (
                <button className="user-action-btn" onClick={handleUnsuspend}>
                  Unsuspend
                </button>
              ) : (
                <button className="user-action-btn warning" onClick={() => setShowSuspendModal(true)}>
                  Suspend
                </button>
              )}
              <button className="user-action-btn" onClick={handleResetPassword}>
                Reset Password
              </button>
              <button className="user-action-btn danger" onClick={handleDelete}>
                Delete User
              </button>
            </div>
          </div>

          {/* Suspension Info (if suspended) */}
          {user.isSuspended && (
            <div className="user-suspension-card">
              <h3>Suspension Details</h3>
              <div className="suspension-info">
                <div className="suspension-row">
                  <span className="suspension-label">Suspended At</span>
                  <span className="suspension-value">{formatDateTime(user.suspendedAt)}</span>
                </div>
                {user.suspendedReason && (
                  <div className="suspension-row">
                    <span className="suspension-label">Reason</span>
                    <span className="suspension-value">{user.suspendedReason}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Additional Info */}
        <div className="user-details-right">
          {/* Company Card (for Customers) */}
          {isCustomer && user.company && (
            <div className="user-company-card">
              <h3>Company Information</h3>
              <div className="company-info">
                <div className="company-header">
                  {user.company.logo ? (
                    <img src={user.company.logo} alt={user.company.name} className="company-logo" />
                  ) : (
                    <div className="company-logo-placeholder">
                      {user.company.name?.[0] || 'C'}
                    </div>
                  )}
                  <div className="company-name-wrap">
                    <h4>{user.company.name}</h4>
                    {user.company.slug && <span className="company-slug">@{user.company.slug}</span>}
                  </div>
                </div>
                {user.company.contact && (
                  <div className="company-details">
                    {user.company.contact.email && (
                      <div className="company-row">
                        <span className="company-label">Email</span>
                        <span className="company-value">{user.company.contact.email}</span>
                      </div>
                    )}
                    {user.company.contact.phone && (
                      <div className="company-row">
                        <span className="company-label">Phone</span>
                        <span className="company-value">{user.company.contact.phone}</span>
                      </div>
                    )}
                  </div>
                )}
                {user.company.address && (
                  <div className="company-address">
                    <span className="company-label">Address</span>
                    <span className="company-value">
                      {[
                        user.company.address.street,
                        user.company.address.city,
                        user.company.address.state,
                        user.company.address.zipCode,
                      ].filter(Boolean).join(', ')}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Driver Profile (for Drivers) */}
          {isDriver && user.driverProfile && (
            <div className="user-driver-card">
              <h3>Driver Profile</h3>
              <div className="driver-info">
                <div className="driver-row">
                  <span className="driver-label">Vehicle</span>
                  <span className="driver-value">{user.driverProfile.vehicle || 'Not specified'}</span>
                </div>
                <div className="driver-row">
                  <span className="driver-label">License</span>
                  <span className="driver-value">{user.driverProfile.licenseNumber || 'Not provided'}</span>
                </div>
                <div className="driver-row">
                  <span className="driver-label">Status</span>
                  <span className={`driver-status ${user.driverProfile.status?.toLowerCase() || 'offline'}`}>
                    {user.driverProfile.status || 'Offline'}
                  </span>
                </div>
                <div className="driver-stats">
                  <div className="driver-stat">
                    <span className="stat-value">{user.driverProfile.totalDeliveries || 0}</span>
                    <span className="stat-label">Total</span>
                  </div>
                  <div className="driver-stat">
                    <span className="stat-value">{user.driverProfile.completedDeliveries || 0}</span>
                    <span className="stat-label">Completed</span>
                  </div>
                  <div className="driver-stat">
                    <span className="stat-value">{user.rating?.toFixed(1) || '0.0'}</span>
                    <span className="stat-label">Rating</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Recent Jobs (for Customers) */}
          {isCustomer && (
            <div className="user-jobs-card">
              <div className="jobs-card-header">
                <h3>Recent Jobs</h3>
                {jobs.length > 0 && (
                  <button className="view-all-btn" onClick={() => navigate(`/jobs?client=${id}`)}>
                    View All
                  </button>
                )}
              </div>
              {jobsLoading ? (
                <div className="jobs-loading">Loading jobs...</div>
              ) : jobs.length === 0 ? (
                <div className="jobs-empty">No jobs found</div>
              ) : (
                <div className="jobs-list">
                  {jobs.slice(0, 5).map((job) => (
                    <div
                      key={job._id}
                      className="job-item"
                      onClick={() => navigate(`/jobs/${job._id}`)}
                    >
                      <div className="job-item-info">
                        <span className="job-item-id">{job.jobId || `#${job._id?.slice(-4)}`}</span>
                        <span className="job-item-route">
                          {job.from?.name || job.from?.address?.substring(0, 15) || 'N/A'} →{' '}
                          {job.to?.name || job.to?.address?.substring(0, 15) || 'N/A'}
                        </span>
                      </div>
                      <span className={`job-item-status ${getStatusClass(job.status)}`}>
                        {job.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Account Info */}
          <div className="user-account-card">
            <h3>Account Information</h3>
            <div className="account-info">
              <div className="account-row">
                <span className="account-label">User ID</span>
                <span className="account-value user-id">{user._id}</span>
              </div>
              <div className="account-row">
                <span className="account-label">Auth Provider</span>
                <span className="account-value">{user.authProvider || 'Email'}</span>
              </div>
              <div className="account-row">
                <span className="account-label">Email Verified</span>
                <span className={`account-value ${user.isEmailVerified ? 'verified' : 'not-verified'}`}>
                  {user.isEmailVerified ? 'Yes' : 'No'}
                </span>
              </div>
              {user.lastLoginIP && (
                <div className="account-row">
                  <span className="account-label">Last Login IP</span>
                  <span className="account-value">{user.lastLoginIP}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="users-modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="users-modal" onClick={(e) => e.stopPropagation()}>
            <div className="users-modal-header">
              <h3>Edit User</h3>
              <button className="users-modal-close" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <div className="users-modal-body">
              <div className="users-form">
                <div className="users-form-row">
                  <div className="users-form-group">
                    <label>First Name *</label>
                    <input
                      type="text"
                      value={editForm.firstName}
                      onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                    />
                  </div>
                  <div className="users-form-group">
                    <label>Last Name</label>
                    <input
                      type="text"
                      value={editForm.lastName}
                      onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                    />
                  </div>
                </div>
                <div className="users-form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  />
                </div>
                <div className="users-form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={editForm.phone}
                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  />
                </div>
                <div className="users-form-group">
                  <label>Role</label>
                  <select
                    value={editForm.role}
                    onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                  >
                    <option value="">Select Role</option>
                    {roles.map((role) => (
                      <option key={role._id} value={role._id}>{role.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="users-modal-footer">
              <button className="users-btn-outline" onClick={() => setShowEditModal(false)}>
                Cancel
              </button>
              <button
                className="users-btn-primary"
                onClick={handleUpdateUser}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Suspend Modal */}
      {showSuspendModal && (
        <div className="users-modal-overlay" onClick={() => setShowSuspendModal(false)}>
          <div className="users-modal" onClick={(e) => e.stopPropagation()}>
            <div className="users-modal-header">
              <h3>Suspend User</h3>
              <button className="users-modal-close" onClick={() => setShowSuspendModal(false)}>×</button>
            </div>
            <div className="users-modal-body">
              <div className="users-suspend-info">
                <p>You are about to suspend:</p>
                <div className="users-suspend-user">
                  <strong>{user.firstName} {user.lastName}</strong>
                  <span>{user.email}</span>
                </div>
                <p className="users-suspend-warning">
                  This user will not be able to log in or access any services until unsuspended.
                </p>
              </div>
              <div className="users-form-group">
                <label>Reason for Suspension</label>
                <textarea
                  value={suspendReason}
                  onChange={(e) => setSuspendReason(e.target.value)}
                  placeholder="Enter reason for suspension (optional)"
                  rows="3"
                />
              </div>
            </div>
            <div className="users-modal-footer">
              <button className="users-btn-outline" onClick={() => setShowSuspendModal(false)}>
                Cancel
              </button>
              <button
                className="users-btn-danger"
                onClick={handleSuspend}
                disabled={saving}
              >
                {saving ? 'Suspending...' : 'Suspend User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
